import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Lock, ArrowLeft, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if user is admin (from /admin login)
const isAdminUser = () => {
  return !!sessionStorage.getItem('admin_auth');
};

export const LessonWatch = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [lesson, setLesson] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [checkingAccess, setCheckingAccess] = useState(false);
  
  // Admin bypass
  const adminAccess = isAdminUser();

  // Check if coming from successful purchase
  const sessionId = searchParams.get('session_id');
  const purchaseEmail = searchParams.get('email');

  useEffect(() => {
    // Admin users have full access
    if (adminAccess) {
      loadLessonForAdmin();
      return;
    }
    
    // If we have session_id and email from purchase, confirm and set access
    if (sessionId && purchaseEmail) {
      confirmPurchase(sessionId, purchaseEmail);
    } else {
      // Check localStorage for saved email
      const savedEmail = localStorage.getItem('lesson_access_email');
      if (savedEmail) {
        setEmail(savedEmail);
        checkAccess(savedEmail);
      } else {
        setLoading(false);
      }
    }
  }, [lessonId, sessionId, purchaseEmail, adminAccess]);

  const loadLessonForAdmin = async () => {
    try {
      const response = await axios.get(`${API}/lessons/${lessonId}`);
      setLesson(response.data);
      setHasAccess(true);
    } catch (error) {
      console.error('Error loading lesson:', error);
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const confirmPurchase = async (sessionId, email) => {
    try {
      // Confirm the purchase
      await axios.post(`${API}/lessons/confirm-purchase`, {
        session_id: sessionId,
        lesson_id: lessonId,
        customer_email: email
      });
      
      // Save email for future access
      localStorage.setItem('lesson_access_email', email);
      setEmail(email);
      
      // Now check access
      await checkAccess(email);
      toast.success('Purchase confirmed! Enjoy your lesson.');
    } catch (error) {
      console.error('Error confirming purchase:', error);
      toast.error('Error confirming purchase. Please contact support.');
      setLoading(false);
    }
  };

  const checkAccess = async (userEmail) => {
    setCheckingAccess(true);
    try {
      const response = await axios.get(`${API}/lessons/check-access/${lessonId}?email=${encodeURIComponent(userEmail)}`);
      
      if (response.data.has_access) {
        setHasAccess(true);
        setLesson(response.data.lesson);
      } else {
        setHasAccess(false);
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
      setCheckingAccess(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      localStorage.setItem('lesson_access_email', email);
      checkAccess(email);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading lesson...</p>
        </div>
      </div>
    );
  }

  // If no access, show email check or purchase prompt
  if (!hasAccess) {
    return (
      <div className="min-h-screen py-12 px-6">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/samba-lessons')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Lessons
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/50 rounded-2xl p-8 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-primary" />
            </div>
            
            <h1 className="text-2xl font-bold text-foreground mb-4">
              This Lesson Requires Purchase
            </h1>
            
            <p className="text-muted-foreground mb-6">
              Already purchased? Enter your email below to access your lesson.
              <br />
              Don't have access? Purchase the lesson to start learning!
            </p>

            <form onSubmit={handleEmailSubmit} className="mb-6">
              <div className="flex gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your purchase email"
                  className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:border-primary"
                  required
                />
                <button
                  type="submit"
                  disabled={checkingAccess}
                  className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold transition-colors disabled:opacity-50"
                >
                  {checkingAccess ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Check Access'}
                </button>
              </div>
            </form>

            <div className="border-t border-border/50 pt-6">
              <button
                onClick={() => navigate('/samba-lessons')}
                className="px-8 py-4 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground rounded-full font-bold text-lg transition-all"
              >
                Purchase This Lesson
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // User has access - show the video
  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate('/samba-lessons')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Lessons
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Video Player */}
          <div className="bg-card border border-border/50 rounded-2xl overflow-hidden mb-6">
            <div className="aspect-video bg-black relative">
              {lesson?.video_url ? (
                <iframe
                  src={lesson.video_url}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={lesson.title}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <Play className="w-16 h-16 text-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">Video player placeholder</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      (Replace with actual Vimeo video URL in admin)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lesson Info */}
          <div className="bg-card border border-border/50 rounded-2xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-green-500 font-semibold">Purchased</span>
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  {lesson?.title}
                </h1>
                <div className="flex items-center gap-4 text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {lesson?.duration}
                  </span>
                </div>
              </div>
              <img
                src={lesson?.thumbnail}
                alt={lesson?.title}
                className="w-24 h-24 rounded-xl object-cover"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LessonWatch;
